import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { getCurrentUser } from '@/services/api/auth';
import type { AppIdEmUsoDetalhe } from '@/services/api/connections';
import AppConflitoDialog from './components/AppConflitoDialog';

/**
 * Texto de erro seguro para o toast.
 *
 * `detail` da API às vezes é string e às vezes é objeto (o 409 de app_id
 * repetido, por exemplo). Jogar o objeto direto no toast renderiza
 * "[object Object]" ou quebra o React.
 */
function mensagemDeErro(error: any, padrao: string): string {
    const d = error?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object' && typeof d.mensagem === 'string') return d.mensagem;
    return padrao;
}
import {
    getMetaApps,
    reactivateMetaApp,
    lerConflitoDeApp,
    createMetaApp,
    updateMetaApp,
    updateMetaAppStatus,
    deleteMetaApp,
    getMetaUsers,
    revokeMetaUser,
    initMetaOAuth,
    syncMetaPages,
    updateMetaPageStatus,
    updateMetaPageConfig,
    getPageCategories,
    getMetaAppForEdit,
} from '@/services/api';
import { getMetaPages, deleteMetaPage } from '@/services/api/connections';
import {
    MetaApp,
    MetaUser,
    MetaPage,
    MetaConnectionTab,
    MetaAppFormData,
    CreateMetaAppInput,
    UpdatePageConfigInput,
} from './types/meta.types';

// Components
import PagesList from './components/PagesList';
import CategoriesList from './components/CategoriesList';
import AccountsList from './components/AccountsList';
import BroadcastV2SettingsPanel from './components/BroadcastV2SettingsPanel';
import OrchestratorSettingsPanel from './components/OrchestratorSettingsPanel';
import AppPageGroupsList from './components/AppPageGroupsList';
import ContingencyPanel from './components/ContingencyPanel';
import CreateEditAppDialog from './components/CreateEditAppDialog';
import ConnectAccountDialog from './components/ConnectAccountDialog';
import DeleteAppDialog from './components/DeleteAppDialog';
import ConfigurePageDialog from './components/ConfigurePageDialog';
import { SyncLeadsDialog, FloatingSyncStatus } from '../Leads/components';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

export default function Connection() {
    const [activeTab, setActiveTab] = useState<MetaConnectionTab>('pages');
    const [isCreateAppDialogOpen, setIsCreateAppDialogOpen] = useState(false);
    const [isEditAppDialogOpen, setIsEditAppDialogOpen] = useState(false);
    const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);
    const [isDeleteAppDialogOpen, setIsDeleteAppDialogOpen] = useState(false);
    const [selectedApp, setSelectedApp] = useState<MetaApp | null>(null);
    const [appToDelete, setAppToDelete] = useState<MetaApp | null>(null);
    const [authToRevoke, setAuthToRevoke] = useState<MetaUser | null>(null);
    const [pageToConfigure, setPageToConfigure] = useState<MetaPage | null>(null);
    const [isConfigurePageDialogOpen, setIsConfigurePageDialogOpen] = useState(false);
    const [isSyncLeadsDialogOpen, setIsSyncLeadsDialogOpen] = useState(false);
    const [pageToSync, setPageToSync] = useState<MetaPage | null>(null);
    const [pageToDelete, setPageToDelete] = useState<MetaPage | null>(null);
    const [activeSyncTaskId, setActiveSyncTaskId] = useState<string | null>(null);
    const [selectedAppIdForConnect, setSelectedAppIdForConnect] = useState<number | null>(null);
    const [appFormData, setAppFormData] = useState<MetaAppFormData>({
        app_name: '',
        app_id: '',
        app_secret: '',
        redirect_uri: import.meta.env.VITE_META_REDIRECT_URI || 'https://meta-api.rocketgroup.io/api/v1/meta/oauth/callback',
        api_version: 'v18.0',
        verify_token: undefined,
        purpose: 'both',
    });

    const { toast } = useToast();
    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const currentUser = getCurrentUser();
    const isAdmin = currentUser?.role === 'admin';

    // Garante que a aba "Páginas" fique ativa ao entrar via link com ?filter=problem
    // (ex: sino de notificação de páginas com token com problema).
    useEffect(() => {
        if (searchParams.get('filter') === 'problem') {
            setActiveTab('pages');
        }
    }, [searchParams]);

    // Handle OAuth callback result
    useEffect(() => {
        const state = location.state as { oauthSuccess?: boolean; oauthError?: string; needsTesterAccess?: boolean } | null;
        if (!state) return;

        if (state.oauthSuccess) {
            if (state.needsTesterAccess) {
                toast({
                    title: '⚠️ Nenhuma página encontrada',
                    description:
                        'O app está em modo de desenvolvimento. Peça ao administrador para adicionar ' +
                        'sua conta como Tester em developers.facebook.com e tente sincronizar novamente.',
                });
            } else {
                toast({
                    title: 'Conta conectada!',
                    description: 'A autenticação com o Meta foi realizada com sucesso.',
                });
            }
            queryClient.invalidateQueries({ queryKey: ['meta-users'] });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
        } else if (state.oauthError) {
            toast({
                variant: 'destructive',
                title: 'Erro na autenticação',
                description: state.oauthError,
            });
        }

        // Clear navigation state to prevent re-triggering on refresh
        navigate(location.pathname, { replace: true, state: null });
    }, [location.state]);



    // Queries
    const { data: apps = [], isLoading: isLoadingApps } = useQuery({
        queryKey: ['meta-apps'],
        queryFn: () => getMetaApps('ativos'),
    });

    const { data: authentications = [], isLoading: isLoadingAuths } = useQuery({
        queryKey: ['meta-users'],
        queryFn: getMetaUsers,
    });

    const { data: pages = [], isLoading: isLoadingPages } = useQuery({
        queryKey: ['meta-pages'],
        queryFn: getMetaPages,
    });

    const { data: categories = [] } = useQuery({
        queryKey: ['page-categories'],
        queryFn: getPageCategories,
    });

    // Apps DESATIVADOS. Vêm em query própria porque "deletar app" é soft
    // delete: eles continuam ocupando o app_id e só podem ser recuperados se
    // estiverem visíveis em algum lugar.
    const { data: appsInativos = [] } = useQuery({
        queryKey: ['meta-apps', 'inativos'],
        queryFn: () => getMetaApps('inativos'),
    });

    const [conflitoApp, setConflitoApp] = useState<AppIdEmUsoDetalhe | null>(null);

    const reativarAppMutation = useMutation({
        mutationFn: reactivateMetaApp,
        onSuccess: (r) => {
            toast({ title: 'App reativada!', description: r?.message || 'A app voltou para a lista de ativas.' });
            queryClient.invalidateQueries({ queryKey: ['meta-apps'] });
            setConflitoApp(null);
            handleCloseCreateAppDialog();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao reativar app',
                description: mensagemDeErro(error, 'Não foi possível reativar a app.'),
            });
        },
    });

    // Mutations - Apps
    const createAppMutation = useMutation({
        mutationFn: createMetaApp,
        onSuccess: () => {
            toast({
                title: 'App criada!',
                description: 'A aplicação foi criada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-apps'] });
            handleCloseCreateAppDialog();
        },
        onError: (error: any) => {
            // O 409 de app_id repetido vira diálogo, não toast: ele tem ação
            // (reativar) e precisa mostrar qual app está conflitando.
            const conflito = lerConflitoDeApp(error);
            if (conflito) {
                setConflitoApp(conflito);
                return;
            }
            toast({
                variant: 'destructive',
                title: 'Erro ao criar app',
                description: mensagemDeErro(error, 'Ocorreu um erro ao criar a app.'),
            });
        },
    });

    const updateAppMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => updateMetaApp(id, data),
        onSuccess: () => {
            toast({
                title: 'App atualizada!',
                description: 'A aplicação foi atualizada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-apps'] });
            handleCloseEditAppDialog();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar app',
                description: error.response?.data?.detail || 'Ocorreu um erro ao atualizar a app.',
            });
        },
    });

    const deleteAppMutation = useMutation({
        mutationFn: deleteMetaApp,
        onSuccess: () => {
            toast({
                title: 'App excluída!',
                description: 'A aplicação foi excluída com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-apps'] });
            handleCloseDeleteAppDialog();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao excluir app',
                description: error.response?.data?.detail || 'Ocorreu um erro ao excluir a app.',
            });
        },
    });

    const toggleAppStatusMutation = useMutation({
        mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
            updateMetaAppStatus(id, is_active),
        onSuccess: () => {
            toast({
                title: 'Status atualizado!',
                description: 'O status da app foi atualizado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-apps'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar status',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation - OAuth
    const initOAuthMutation = useMutation({
        mutationFn: ({ appId, scopes }: { appId: number | null; scopes?: string[] }) =>
            initMetaOAuth(appId, scopes),
        onSuccess: (data) => {
            // Redireciona para autorização do Facebook
            window.location.href = data.authorization_url;
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao iniciar OAuth',
                description: error.response?.data?.detail || 'Ocorreu um erro ao conectar.',
            });
        },
    });

    // Mutation - Sync Pages
    const syncPagesMutation = useMutation({
        mutationFn: syncMetaPages,
        onSuccess: (data) => {
            toast({
                title: 'Páginas sincronizadas!',
                description: `${data.created} criadas, ${data.updated} atualizadas.`,
            });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
            queryClient.invalidateQueries({ queryKey: ['meta-users'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao sincronizar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation - Revoke Authentication
    const revokeAuthMutation = useMutation({
        mutationFn: revokeMetaUser,
        onSuccess: () => {
            toast({
                title: 'Perfil removido!',
                description: 'A autenticação foi revogada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-users'] });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao remover perfil',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation - Toggle Page Status
    const togglePageStatusMutation = useMutation({
        mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
            updateMetaPageStatus(id, is_active),
        onSuccess: () => {
            toast({
                title: 'Status atualizado!',
                description: 'O status da página foi atualizado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar status',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation - Update Page Config
    const updatePageConfigMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdatePageConfigInput }) =>
            updateMetaPageConfig(id, data),
        onSuccess: () => {
            toast({
                title: 'Configuração salva!',
                description: 'As configurações da página foram atualizadas com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
            setIsConfigurePageDialogOpen(false);
            setPageToConfigure(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar configuração',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation - Delete Page
    const deletePageMutation = useMutation({
        mutationFn: (id: number) => deleteMetaPage(id),
        onSuccess: () => {
            toast({
                title: 'Página removida!',
                description: 'A página foi removida com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
            setPageToDelete(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao remover página',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Handlers - Apps
    const handleCreateAppSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const redirectUri = appFormData.redirect_uri || import.meta.env.VITE_META_REDIRECT_URI || 'https://meta-api.rocketgroup.io/api/v1/meta/oauth/callback';

        const appData: CreateMetaAppInput = {
            app_name: appFormData.app_name,
            app_id: appFormData.app_id,
            app_secret: appFormData.app_secret,
            redirect_uri: redirectUri,
            api_version: appFormData.api_version || 'v18.0',
            scopes: appFormData.scopes,
            purpose: appFormData.purpose || 'both',
        };

        createAppMutation.mutate(appData);
    };

    const handleEditAppSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedApp) return;

        const redirectUri = appFormData.redirect_uri || import.meta.env.VITE_META_REDIRECT_URI || 'https://meta-api.rocketgroup.io/api/v1/meta/oauth/callback';

        updateAppMutation.mutate({
            id: selectedApp.id,
            data: {
                app_name: appFormData.app_name,
                app_id: appFormData.app_id,
                app_secret: appFormData.app_secret,
                redirect_uri: redirectUri,
                api_version: appFormData.api_version,
                scopes: appFormData.scopes,
                purpose: appFormData.purpose,
            },
        });
    };

    const handleOpenEditAppDialog = async (app: MetaApp) => {
        try {
            // Busca o app com app_secret REAL (não mascarado) — só nesse momento,
            // nunca na listagem geral, que continua mascarada por segurança.
            const fullApp = await getMetaAppForEdit(app.id);
            setSelectedApp(fullApp);
            setAppFormData({
                app_name: fullApp.app_name,
                app_id: fullApp.app_id,
                app_secret: fullApp.app_secret,
                redirect_uri: fullApp.redirect_uri,
                api_version: fullApp.api_version,
                verify_token: fullApp.verify_token,
                purpose: fullApp.purpose ?? 'both',
            });
            setIsEditAppDialogOpen(true);
        } catch (error: any) {
            toast({
                variant: 'destructive',
                title: 'Erro ao carregar app',
                description: error.response?.data?.detail || 'Não foi possível carregar os dados da app para edição.',
            });
        }
    };

    const handleOpenDeleteAppDialog = (app: MetaApp) => {
        setAppToDelete(app);
        setIsDeleteAppDialogOpen(true);
    };

    const handleConfirmDeleteApp = () => {
        if (appToDelete) {
            deleteAppMutation.mutate(appToDelete.id);
        }
    };

    const handleToggleAppStatus = (app: MetaApp) => {
        if (app.is_default) {
            toast({
                variant: 'destructive',
                title: 'Ação não permitida',
                description: 'Não é possível desativar a app padrão do sistema.',
            });
            return;
        }

        toggleAppStatusMutation.mutate({
            id: app.id,
            is_active: !app.is_active,
        });
    };

    const handleCloseCreateAppDialog = () => {
        setIsCreateAppDialogOpen(false);
        setAppFormData({
            app_name: '',
            app_id: '',
            app_secret: '',
            redirect_uri: import.meta.env.VITE_META_REDIRECT_URI || '',
            api_version: 'v18.0',
            purpose: 'both',
        });
    };

    const handleCloseEditAppDialog = () => {
        setIsEditAppDialogOpen(false);
        setSelectedApp(null);
        setAppFormData({
            app_name: '',
            app_id: '',
            app_secret: '',
            redirect_uri: import.meta.env.VITE_META_REDIRECT_URI || '',
            api_version: 'v18.0',
            purpose: 'both',
        });
    };

    const handleCloseDeleteAppDialog = () => {
        setIsDeleteAppDialogOpen(false);
        setAppToDelete(null);
    };

    // Handlers - OAuth
    const handleOpenConnectDialog = () => {
        setSelectedAppIdForConnect(null);
        setIsConnectDialogOpen(true);
    };

    const handleConnect = () => {
        const app = apps.find((a) => a.id === selectedAppIdForConnect);
        initOAuthMutation.mutate({ appId: selectedAppIdForConnect, scopes: app?.oauth_scopes });
    };

    // Handlers - Auth
    const handleSyncPages = (auth: MetaUser) => {
        syncPagesMutation.mutate({ meta_app_id: auth.meta_app_id });
    };

    const handleRevokeAuth = (auth: MetaUser) => {
        setAuthToRevoke(auth);
    };

    const handleConfirmRevoke = () => {
        if (authToRevoke) {
            revokeAuthMutation.mutate(authToRevoke.id, {
                onSettled: () => setAuthToRevoke(null),
            });
        }
    };

    const handleTogglePageStatus = (page: MetaPage) => {
        togglePageStatusMutation.mutate({
            id: page.id,
            is_active: !page.is_active,
        });
    };

    const handleOpenConfigurePage = (page: MetaPage) => {
        setPageToConfigure(page);
        setIsConfigurePageDialogOpen(true);
    };

    const handleSavePageConfig = (pageId: number, data: UpdatePageConfigInput) => {
        updatePageConfigMutation.mutate({ id: pageId, data });
    };

    const handleOpenSyncLeads = (page: MetaPage) => {
        setPageToSync(page);
        setIsSyncLeadsDialogOpen(true);
    };

    const handleSyncLeadsComplete = () => {
        queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
    };

    const handleDeletePage = (page: MetaPage) => {
        setPageToDelete(page);
    };

    const handleConfirmDeletePage = () => {
        if (pageToDelete) {
            deletePageMutation.mutate(pageToDelete.id);
        }
    };

    const handleBackgroundSync = (taskId: string) => {
        setActiveSyncTaskId(taskId);
        setIsSyncLeadsDialogOpen(false);
    };

    // Troca manual de aba: some com o filtro ?filter=problem ao sair da aba "Páginas",
    // pois ele deixa de fazer sentido fora desse contexto.
    const handleTabChange = (tabId: MetaConnectionTab) => {
        setActiveTab(tabId);
        if (tabId !== 'pages' && searchParams.has('filter')) {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.delete('filter');
                return params;
            }, { replace: true });
        }
    };

    const isLoading = isLoadingApps || isLoadingAuths || isLoadingPages;

    return (
        <div className="space-y-6">
            {/* Dark Gradient Header — padrão das outras páginas Meta */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a] p-8 shadow-xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#7dd3fc]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#38bdf8]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />
                <div className="relative flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10">
                        <svg className="w-6 h-6 text-[#7dd3fc]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Configurações Meta</h1>
                        <p className="mt-1 text-sm text-slate-300">Gerencie páginas, categorias e contas do Meta Ads</p>
                    </div>
                </div>
            </div>

            {/* Content: Sidebar + Main */}
            <div className="flex gap-6">
                {/* Vertical Sidebar Menu */}
                <div className="w-52 flex-shrink-0 flex flex-col gap-1">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-2">Menu</p>
                    {([
                        { id: 'pages', label: 'Páginas' },
                        { id: 'categories', label: 'Categorias' },
                        { id: 'accounts', label: 'Contas e Usuários' },
                        { id: 'app-groups', label: 'Grupos de App' },
                        { id: 'contingency', label: 'Contingência' },
                        { id: 'broadcast-v2', label: 'Config' },
                        { id: 'orchestrator', label: 'Orquestrador' },
                    ] as { id: MetaConnectionTab; label: string }[]).map((item) => (
                        <button
                            key={item.id}
                            onClick={() => handleTabChange(item.id)}
                            className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${activeTab === item.id
                                ? 'bg-blue-50 text-blue-700 border border-blue-100'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                }`}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                {/* Main Content */}
                <div className="flex-1 min-w-0">
                    {isLoading && (
                        <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f172a]"></div>
                                <p className="ml-3 text-gray-500">Carregando configurações...</p>
                            </div>
                        </div>
                    )}

                    {!isLoading && (
                        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                            {activeTab === 'pages' && (
                                <PagesList
                                    pages={pages}
                                    onToggleStatus={handleTogglePageStatus}
                                    onConfigure={handleOpenConfigurePage}
                                    onSyncLeads={handleOpenSyncLeads}
                                    onConnect={handleOpenConnectDialog}
                                    onDeletePage={handleDeletePage}
                                />
                            )}
                            {activeTab === 'categories' && <CategoriesList />}
                            {activeTab === 'app-groups' && <AppPageGroupsList />}
                            {activeTab === 'contingency' && <ContingencyPanel />}
                            {activeTab === 'broadcast-v2' && <BroadcastV2SettingsPanel />}
                            {activeTab === 'orchestrator' && <OrchestratorSettingsPanel />}
                            {activeTab === 'accounts' && (
                                <AccountsList
                                    apps={apps}
                                    appsInativos={appsInativos}
                                    onReativarApp={(app) => reativarAppMutation.mutate(app.id)}
                                    reativandoAppId={
                                        reativarAppMutation.isPending
                                            ? (reativarAppMutation.variables as number)
                                            : null
                                    }
                                    authentications={authentications}
                                    onEditApp={handleOpenEditAppDialog}
                                    onDeleteApp={handleOpenDeleteAppDialog}
                                    onToggleAppStatus={handleToggleAppStatus}
                                    onSync={handleSyncPages}
                                    onRevoke={handleRevokeAuth}
                                    onCreateApp={() => setIsCreateAppDialogOpen(true)}
                                    onConnectAccount={handleOpenConnectDialog}
                                    isAdmin={isAdmin}
                                    currentUserId={currentUser?.id}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Dialogs */}
            <CreateEditAppDialog
                open={isCreateAppDialogOpen}
                onOpenChange={(open) => { if (!open) handleCloseCreateAppDialog(); else setIsCreateAppDialogOpen(true); }}
                formData={appFormData}
                onFormChange={setAppFormData}
                onSubmit={handleCreateAppSubmit}
                isLoading={createAppMutation.isPending}
            />
            <CreateEditAppDialog
                open={isEditAppDialogOpen}
                onOpenChange={(open) => { if (!open) handleCloseEditAppDialog(); else setIsEditAppDialogOpen(true); }}
                formData={appFormData}
                onFormChange={setAppFormData}
                onSubmit={handleEditAppSubmit}
                isLoading={updateAppMutation.isPending}
                isEdit
            />
            <ConnectAccountDialog
                open={isConnectDialogOpen}
                onOpenChange={setIsConnectDialogOpen}
                apps={apps}
                selectedAppId={selectedAppIdForConnect}
                onAppSelect={setSelectedAppIdForConnect}
                onConnect={handleConnect}
                isLoading={initOAuthMutation.isPending}
                selectedApp={apps.find((a) => a.id === selectedAppIdForConnect)}
            />
            {/* Cadastro de app_id que já existe: diálogo com saída, não toast
                vermelho. Ver AppConflitoDialog. */}
            <AppConflitoDialog
                conflito={conflitoApp}
                reativando={reativarAppMutation.isPending}
                onReativar={(id) => reativarAppMutation.mutate(id)}
                onClose={() => setConflitoApp(null)}
            />
            <DeleteAppDialog
                open={isDeleteAppDialogOpen}
                onOpenChange={(open) => { if (!open) handleCloseDeleteAppDialog(); else setIsDeleteAppDialogOpen(true); }}
                app={appToDelete}
                onConfirm={handleConfirmDeleteApp}
                isLoading={deleteAppMutation.isPending}
            />
            <ConfigurePageDialog
                open={isConfigurePageDialogOpen}
                onOpenChange={setIsConfigurePageDialogOpen}
                page={pageToConfigure}
                categories={categories}
                onSave={handleSavePageConfig}
                isLoading={updatePageConfigMutation.isPending}
            />
            <SyncLeadsDialog
                open={isSyncLeadsDialogOpen}
                onOpenChange={setIsSyncLeadsDialogOpen}
                pages={pages}
                onSyncComplete={handleSyncLeadsComplete}
                initialSelectedPageId={pageToSync?.id}
                onBackgroundSync={handleBackgroundSync}
            />

            {activeSyncTaskId && (
                <FloatingSyncStatus
                    taskId={activeSyncTaskId}
                    onClose={() => setActiveSyncTaskId(null)}
                />
            )}

            <ConfirmDialog
                open={!!authToRevoke}
                onOpenChange={(open) => { if (!open) setAuthToRevoke(null); }}
                variant="danger"
                title="Excluir conta conectada"
                description={`Tem certeza que deseja excluir a conta "${authToRevoke?.meta_user_name}"? Todas as páginas vinculadas a esta conta também serão excluídas permanentemente. Esta ação não pode ser desfeita.`}
                confirmLabel="Excluir"
                cancelLabel="Cancelar"
                isLoading={revokeAuthMutation.isPending}
                onConfirm={handleConfirmRevoke}
            />
            <ConfirmDialog
                open={!!pageToDelete}
                onOpenChange={(open) => { if (!open) setPageToDelete(null); }}
                variant="danger"
                title="Remover página"
                description={`Tem certeza que deseja remover a página "${pageToDelete?.page_name}"? Esta ação não pode ser desfeita.`}
                confirmLabel="Remover"
                cancelLabel="Cancelar"
                isLoading={deletePageMutation.isPending}
                onConfirm={handleConfirmDeletePage}
            />
        </div>
    );
}
